import React, { useState } from 'react';
import { SkinProps } from '../../../../types/component-skin';

/**
 * 검색창 기본 스킨
 */
const BasicSearchBarSkin: React.FC<SkinProps> = ({ 
  data, 
  actions, 
  options = {},
  utils
}) => {
  const t = utils?.t || ((text: string) => text);
  const [isFocused, setIsFocused] = useState(false);
  
  // 데이터 추출
  const { 
    searchQuery,
    placeholder,
    buttonText,
    isLoading
  } = data;
  
  // 액션 추출
  const {
    handleSearch,
    handleInputChange,
    handleKeyPress
  } = actions;
  
  // 옵션 추출
  const {
    showIcon = true,
    inputHeight = '48px',
    primaryColor = '#007bff',
    buttonColor = '#007bff',
    buttonTextColor = '#ffffff',
    borderRadius = '8px',
    fullWidth = true,
    showShadow = false,
    className = ''
  } = options;
  
  // 스타일 정의
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    width: fullWidth ? '100%' : 'auto',
    maxWidth: fullWidth ? '100%' : '600px',
    margin: '0 auto',
    position: 'relative',
    boxShadow: showShadow && isFocused ? '0 4px 12px rgba(0, 123, 255, 0.15)' : 'none',
    borderRadius: borderRadius,
    transition: 'box-shadow 0.3s ease'
  };
  
  const inputContainerStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    position: 'relative',
    alignItems: 'center'
  };
  
  const clearButtonStyle: React.CSSProperties = {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    color: '#adb5bd',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '4px 8px',
    display: searchQuery ? 'block' : 'none',
    transition: 'color 0.2s ease',
    zIndex: 1
  };
  
  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: inputHeight,
    padding: showIcon ? '0 15px 0 45px' : '0 20px',
    fontSize: '16px',
    fontWeight: '400',
    border: isFocused ? `2px solid ${primaryColor}` : '2px solid #e9ecef',
    borderRadius: borderRadius,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: 'none',
    outline: 'none',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    backgroundColor: isFocused ? '#ffffff' : '#f8f9fa',
    color: '#495057'
  };
  
  const iconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: isFocused ? primaryColor : '#adb5bd',
    fontSize: '18px',
    pointerEvents: 'none',
    transition: 'color 0.3s ease'
  };
  
  const buttonStyle: React.CSSProperties = {
    height: inputHeight,
    padding: '0 30px',
    fontSize: '15px',
    fontWeight: '600',
    color: buttonTextColor,
    backgroundColor: buttonColor || primaryColor,
    border: `2px solid ${primaryColor}`,
    borderLeft: 'none',
    borderRadius: borderRadius,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    minWidth: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px'
  };
  
  return (
    <div className={`search-bar-component ${className}`} style={containerStyle}>
      <div style={inputContainerStyle}>
        {showIcon && (
          <i className="fas fa-search" style={iconStyle}></i>
        )}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t(placeholder)}
          style={inputStyle}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={isLoading}
        />
        {searchQuery && (
          <button
            type="button"
            style={clearButtonStyle}
            onClick={() => handleInputChange('')}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#495057';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#adb5bd';
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>
      <button
        onClick={handleSearch}
        style={buttonStyle}
        onMouseEnter={(e) => {
          if (!isLoading && searchQuery.trim()) {
            e.currentTarget.style.backgroundColor = '#0056b3';
            e.currentTarget.style.borderColor = '#0056b3';
            e.currentTarget.style.transform = 'translateX(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = buttonColor || primaryColor;
          e.currentTarget.style.borderColor = buttonColor || primaryColor;
          e.currentTarget.style.transform = 'translateX(0)';
        }}
        disabled={isLoading || !searchQuery.trim()}
      >
        {isLoading ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        ) : (
          <>
            <span>{t(buttonText)}</span>
            {!isLoading && <i className="fas fa-chevron-right" style={{ fontSize: '12px' }}></i>}
          </>
        )}
      </button>
    </div>
  );
};

export default BasicSearchBarSkin;